// components/Kanban/GestionUsers.js
import { KeyboardSensor, MouseSensor, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useDispatch } from "react-redux";
import { useState } from "react";
import KanbanCard from "../../../../../components/Kanban/Card";
import Kanban from "../../../../../components/Kanban/Kanban";
import { addRoleToUserId, getAllRolesAndUsers, getAllUsers, removeRoleFromUserId } from "../../../store/userManagement/userManagementThunks";

export default function ManageUsers({ selectedEmployeeAvatar, elementsPerPage = 100 }) {
    const [searchTerm, setSearchTerm] = useState("");

    const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
    const mouseSensor = useSensor(MouseSensor);
    const touchSensor = useSensor(TouchSensor);
    const keyboardSensor = useSensor(KeyboardSensor);
    const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor, pointerSensor);

    const dispatch = useDispatch();

    const gestionUsersConfig = {
        getData: async (config, selectedEmployeeAvatar, setters, localUsers, users) => {
            const { setLoading, setColumns, setCards, setLocalCards, setDone } = setters;
            setLoading(true);
            // ... reset state ...

            const tenantId = selectedEmployeeAvatar?.tenant_id || 1;
            const rolesWithUsers = await dispatch(getAllRolesAndUsers()).unwrap();

            const localRoles = rolesWithUsers.map(role => ({
                id: role.id,
                title: role.title,
                description: role.description,
                icon: role.icon,
            }));

            const users2 = [];
            rolesWithUsers.forEach(role => {
                role.user_maps?.forEach((user, index) => {
                    users2.push({
                        id: users2.length,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        user_id: user.id,
                        avatar: user.urlphoto,
                        email: user.email,
                        role: role.id,
                    });
                });
            });

            return [localRoles, users2];
        },

        onCardDragEnd: async (config, users, userId, roles, newRole) => {
            const newRoleId = roles.find(r => r.id === newRole).id;
            const newUser = await users.find(u => u.id === userId);
            await dispatch(addRoleToUserId({ body: { role_id: newRoleId }, params: [{ user_id: newUser.user_id }] }));
            let localUsers = [];
            if (newUser) {
                localUsers.push({
                    id: users.length !== 0 ? users.at(-1).id + 1 : 0,
                    firstName: newUser.firstName,
                    lastName: newUser.lastName,
                    avatar: newUser.avatar,
                    user_id: newUser.user_id,
                    email: newUser.email,
                    role: newRole,
                });
            }
            return users.concat(localUsers);
        },

        filterColumns: (roles, searchColumn) => {
            return roles ? roles.filter((role) => role.title?.toLowerCase().includes(searchColumn?.toLowerCase())) : [];
        },

        renderItem: (item, setIsSelected, selectedIds, columnIdPrefix) => {
            return <KanbanCard
                key={item.id}
                item={item}
                setIsSelected={setIsSelected}
                selectedIds={selectedIds}
                showContent={(localAvatar, user) =>
                    <>
                        <img src={localAvatar} alt={`${user.firstName !== null ? user.firstName : user.email}'s avatar`} />
                        <p className="">{user.firstName ? user.firstName : user.email} {user.lastName ? user.lastName.toUpperCase() : ""}</p>
                    </>
                }
                avatar={true}
                columnIdPrefix={columnIdPrefix}
            />
        },

        renderItemFilter: (allItems, handleNewUser, setIsSelected, selectedIds, columnIdPrefix, currentPage, elementsPerPage) =>
            allItems
                .slice((currentPage - 1) * elementsPerPage, currentPage * elementsPerPage)
                .map((user) => {
                    return (
                        <KanbanCard
                            key={user.id}
                            item={user}
                            setIsSelected={setIsSelected}
                            selectedIds={selectedIds}
                            showContent={(localAvatar, user) =>
                                <>
                                    <img src={localAvatar} alt={`${user.firstName !== null ? user.firstName : user.email}'s avatar`} />
                                    <p className="">{user.firstName ? user.firstName : user.email} {user.lastName ? user.lastName.toUpperCase() : ""}</p>
                                </>
                            }
                            avatar={true}
                            clickAction={() => handleNewUser(user)}
                            columnIdPrefix={columnIdPrefix}
                        />
                    );
                }),

        filterCards: (users, searchTerm, role) => {
            return users.filter((user) => {
                // Skip if user doesn't match role
                if (user.role !== role.id) return false;

                // Normalize and tokenize search term
                const searchTermTokens = (searchTerm || '')
                    .toLowerCase()
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean); // Remove empty tokens

                // Early return if no search term
                if (searchTermTokens.length === 0) return true;

                // Normalize user fields
                const firstName = (user.firstName || '').toLowerCase();
                const lastName = (user.lastName || '').toLowerCase();
                const email = (user.email || '').toLowerCase();

                // Build full name (space-separated, omitting empty parts)
                const fullName = [firstName, lastName].filter(Boolean).join(' ');

                // Tokenize testable fields
                const nameTokens = fullName.split(/\s+/).filter(Boolean);
                const firstNameTokens = firstName.split(/\s+/).filter(Boolean);
                const lastNameTokens = lastName.split(/\s+/).filter(Boolean);
                const emailTokens = email.split(/\s+/).filter(Boolean);

                // Helper: Check if all search tokens are found in any of the given token arrays
                const matchesTokens = (tokensToSearchIn) => {
                    return searchTermTokens.every(searchToken =>
                        tokensToSearchIn.some(fieldToken =>
                            fieldToken.includes(searchToken)
                        )
                    );
                };

                // Try matching against:
                // 1. Full name tokens
                // 2. First name tokens (if full name didn't match)
                // 3. Last name tokens (if full name didn't match)
                // 4. Email tokens — only if both first & last name are empty
                if (matchesTokens(nameTokens)) {
                    return true;
                }

                if (matchesTokens(firstNameTokens) || matchesTokens(lastNameTokens)) {
                    return true;
                }

                // Fallback to email only if no name parts exist
                if (!firstName && !lastName && matchesTokens(emailTokens)) {
                    return true;
                }

                return false;
            });
        },

        filterAllCards: (allItems, items, column) => {
            return allItems
                .filter(u => u.firstName && u.lastName && u.urlphoto)
                .filter(u => items.find(i => i.user_id === u.id && i.role === column.id) === undefined)
        },

        getAllExternalItems: async (config, selectedEmployeeAvatar) => {
            const localUsers = await dispatch(getAllUsers());
            const allUsers = localUsers?.payload || [];
            return allUsers;
        },

        onAddItem: async (config, users, user, role) => {
            let localUsers = [...users];
            await dispatch(addRoleToUserId({ params: [{ user_id: user.id, role_id: role.id }] }));
            localUsers.push({
                id: localUsers.length !== 0 ? localUsers.at(-1).id + 1 : 0,
                firstName: user.firstName,
                lastName: user.lastName,
                user_id: user.id,
                avatar: user.urlphoto,
                email: user.email,
                role: role.id,
            });
            return localUsers;
        },

        onDeleteItems: async (config, selectedIds, users, role) => {
            // Create a new array for the API calls
            const apiCalls = selectedIds.map(async (id) => {
                const user = users.find(u => u.id === id);
                if (user) {
                    await dispatch(removeRoleFromUserId({
                        params: [{ user_id: user.user_id, role_id: role.id }]
                    }));
                }
            });

            // Wait for all API calls to complete
            await Promise.all(apiCalls);

            // Create a new array by filtering out the deleted items
            const filteredUsers = users.filter(u => !selectedIds.includes(u.id));

            return filteredUsers;
        },

        dragOverlayShow: (activeId, users, setIsSelected, columnIdPrefix) => {
            if (!users) return <div>Empty</div>;
            return <KanbanCard
                key={activeId}
                item={users.find((user) => user.id === activeId)}
                setIsSelected={setIsSelected}
                selectedIds={[]}
                showContent={(localAvatar, user) =>
                    <>
                        <img src={localAvatar} alt={`${user.firstName !== null ? user.firstName : user.email}'s avatar`} />
                        <p className="">{user.firstName ? user.firstName : user.email} {user.lastName ? user.lastName.toUpperCase() : ""}</p>
                    </>
                }
                avatar={true}
                columnIdPrefix={columnIdPrefix}
            />
        }
    };

    return (
        <>
            <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            <Kanban
                key={"gestion-users-kanban"}
                config={gestionUsersConfig}
                sensors={sensors}
                searchColumn={searchTerm}
                selectedEmployeeAvatar={selectedEmployeeAvatar}
                columnIdPrefix="gestion-user"
                elementsPerPage={elementsPerPage}
            />
        </>
    )
}
